import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PageHeader, Card, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useNotifications, ago } from "@/components/NotificationBell";
import { GraphiteScope } from "@/components/Graphite";

function EmailSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notification-prefs"], queryFn: async () => (await api.get("/notifications/preferences")).data });
  const save = useMutation({
    mutationFn: async (email) => (await api.put("/notifications/preferences", { email })).data,
    onSuccess: (d) => { qc.setQueryData(["notification-prefs"], d); toast.success("Email settings saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return null;
  return (
    <Card data-testid="email-settings">
      <h2 className="font-display text-h4 font-semibold">Email</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        {data.email_enabled ? <>Sent to <span className="text-foreground">{data.email}</span>. Everything also appears here.</>
          : "Email isn't set up on this server: notifications appear here only."}
      </p>
      <div className="divide-y">
        {data.categories.map((c) => (
          <label key={c.key} className="flex items-center justify-between gap-4 py-3">
            <span>
              {c.label}
              {c.locked && <span className="block text-sm text-muted-foreground">Always sent</span>}
            </span>
            <Switch checked={c.email} disabled={c.locked || !data.email_enabled || save.isPending}
              onCheckedChange={(on) => save.mutate({ [c.key]: on })} data-testid={`email-pref-${c.key}`}
              aria-label={`Email me: ${c.label}`} />
          </label>
        ))}
      </div>
    </Card>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const { data, isLoading, read } = useNotifications(100);
  const items = data?.notifications || [];

  return (
    <GraphiteScope data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="What happened in your workspace, and which of it you get by email"
        actions={data?.unread > 0 && <Button variant="outline" onClick={() => read.mutate({ all: true })}>Mark all read</Button>} />
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          {isLoading ? <Loading /> : items.length === 0 ? <Empty>No notifications yet.</Empty> : (
            <Card className="p-0 overflow-hidden">
              {/* Graphite: unread is a filled dot and a semibold title; rows take the hover wash */}
              {items.map((n) => (
                <button key={n.id}
                  className="w-full text-left px-5 py-4 border-b last:border-0 flex gap-3 transition-colors duration-300 ease-out-expo hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                  onClick={() => { if (!n.read_at) read.mutate({ ids: [n.id] }); navigate(n.link); }}>
                  <span className={cn("w-2 h-2 mt-2 rounded-full shrink-0", !n.read_at && "bg-primary")} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className={cn(!n.read_at && "font-semibold")}>
                        {!n.read_at && <span className="sr-only">Unread: </span>}{n.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{ago(n.created_at)}</span>
                    </span>
                    <span className="block text-sm text-gr-ink-secondary mt-1">{n.body}</span>
                  </span>
                </button>
              ))}
            </Card>
          )}
        </div>
        <EmailSettings />
      </div>
    </GraphiteScope>
  );
}
